import Analytics from '~/services/EventLoggerService';
import CookieService from '~/services/CookieService';
import $store from '~/services/StoreService';
import UserService from '~/services/UserService';
import Subscription from '~/config/Subscription';
import featureFlags from '~/services/FeatureFlagsService';

import getRouter from '~/services/router';
import { isClient } from './EnvUtils';
import Constants from '~/config/Constants';
import { isCountryUS } from '~/utils/ExperimentUtils';
import GrowthConstants from '~/config/GrowthConstants';

export function GetPlansLink(feat, variant) {
  let linkToOpen = '/home/plans?source_cat=product';
  if (feat) {
    const cleanFeat = feat.replace('_', '-');
    linkToOpen += `&feat=${cleanFeat}`;
  }
  if (variant) {
    const cleanVariant = variant.replace('_', '-');
    linkToOpen += `&variant=${cleanVariant}`;
  }
  if ($store()) {
    const user = new UserService($store().getters['root/user'] ?? {});
    if (user && user.id) {
      linkToOpen += `&user_id=${user.id}`;
    }
  }
  return linkToOpen;
}

export function OpenSnDPlansPage(feat, variant) {
  if (isClient() && window) {
    const linkToOpen = GetPlansLink(feat, variant);
    window.open(linkToOpen, '_blank');
  }
}

export function GetPlansLinkUS(feat, variant) {
  const currentPath = window.location.pathname;
  let linkToOpen = `/super-pricing?forSND=true&backto=${currentPath}`;
  if (feat) {
    const cleanFeat = feat.replace('_', '-');
    linkToOpen += `&feat=${cleanFeat}`;
  }
  if (variant) {
    const cleanVariant = variant.replace('_', '-');
    linkToOpen += `&variant=${cleanVariant}`;
  }
  if ($store()) {
    const user = new UserService($store().getters['root/user'] ?? {});
    if (user && user.id) {
      linkToOpen += `&user_id=${user.id}`;
    }
  }
  return linkToOpen;
}
export function OpenSnDPlansPageUS(feat, variant) {
  if (isClient() && window) {
    const linkToOpen = GetPlansLinkUS(feat, variant);
    window.open(linkToOpen, '_blank');
  }
}

export function GetSnDLink(feat, variant) {
  let linkToOpen = '/home/schools-and-districts?source_cat=product';
  if (feat) {
    const cleanFeat = feat.replace('_', '-');
    linkToOpen += `&feat=${cleanFeat}`;
  }
  if (variant) {
    const cleanVariant = variant.replace('_', '-');
    linkToOpen += `&variant=${cleanVariant}`;
  }
  if ($store()) {
    const user = new UserService($store().getters['root/user'] ?? {});
    if (user && user.id) {
      linkToOpen += `&user_id=${user.id}`;
    }
  }
  return linkToOpen;
}

export function OpenSnDPage(feat, variant) {
  if (isClient() && window) {
    const linkToOpen = GetSnDLink(feat, variant);
    window.open(linkToOpen, '_blank');
  }
}

export function GetTypeFormSource({
  source, feat, variant, prefix = '', suffix = '',
}) {
  let val = '';
  const cleanedValues = [];

  /**
   * sequence of these values insertion matters
   * clean source, feat, variance in order
  */
  if (source) {
    const cleanSource = source.replace('_', '-');
    cleanedValues.push(cleanSource);
  }
  if (feat) {
    const cleanFeat = feat.replace('_', '-');
    cleanedValues.push(cleanFeat);
  }
  if (variant) {
    const cleanVariant = variant.replace('_', '-');
    cleanedValues.push(cleanVariant);
  }

  val = cleanedValues.join('_');

  // if prefix is present
  if (prefix && val) {
    const cleanPrefix = prefix.replace('_', '-');
    val = `${cleanPrefix}:${val}`;
  }

  // if suffix is present
  if (suffix && val) {
    const cleanSuffix = suffix.replace('_', '-');
    val = `${val}:${cleanSuffix}`;
  }

  return val;
}

export function OpenTypeForm({
  source, feat, variant, prefix, suffix,
}) {
  if (isClient() && window) {
    let typeFormSource = GetTypeFormSource({
      source, feat, variant, prefix, suffix,
    });

    Analytics.logEvent('quote_form_load', { source: typeFormSource });

    let formEndPoint = 'https://quizizz.typeform.com/to/vZxFA4SC';
    if (isCountryUS()) {
      formEndPoint = 'https://quizizz.typeform.com/to/v5AziGNk';
    }

    const serverData = $store().getters['root/serverData'];
    if (serverData?.requestCountry === 'AU') {
      formEndPoint = 'https://quizizz.typeform.com/to/iKFByhuz';
    } else {
      const sndLeadFormFeatureValue = featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.SND_LEAD_FORM_EXP, 'TYPEFORM');
      // paper form exp
      if (sndLeadFormFeatureValue === 'PAPERFORM') {
        formEndPoint = 'https://quizizz.com/home/new-sign-up-form';
        typeFormSource = `EXP:${typeFormSource}`;
      }

      // paper form control group
      if (sndLeadFormFeatureValue === 'TYPEFORM_EXP') {
        typeFormSource = `CNTR:${typeFormSource}`;
      }
    }

    const FCTypeFormEnabled = featureFlags().isEnabled(GrowthConstants.FeatureFlags.PRICING_PAGE_TYPE_FORM_FC);
    const MCTypeFormEnabled = featureFlags().isEnabled(GrowthConstants.FeatureFlags.PRICING_PAGE_TYPE_FORM_MC);
    if (FCTypeFormEnabled) {
      formEndPoint = 'https://quizizz.typeform.com/to/IYimd8hf';
    }

    if (MCTypeFormEnabled) {
      formEndPoint = 'https://quizizz.typeform.com/to/njUo2S6l';
    }

    const typeFormURL = new URL(formEndPoint);
    typeFormURL.searchParams.set('session_id', CookieService.get('quizizz_uid'));
    typeFormURL.searchParams.set('locale', CookieService.get('locale'));
    typeFormURL.searchParams.set('source', typeFormSource);
    typeFormURL.searchParams.set('source_cat', 'product');
    if ($store()) {
      const user = new UserService($store().getters['root/user'] ?? {});
      if (user && user.id) {
        typeFormURL.searchParams.set('user_id', user.id);
      }
    }

    window.open(typeFormURL, '_blank');
  }
}

export function OpenQFWContactUsTypeForm({ source, feat, variant }, openInNewTab = false) {
  if (isClient() && window) {
    Analytics.logEvent('qfw_contact_us_form_load', { source, feat, variant });

    const typeFormEndpoint = 'https://quizizz.typeform.com/to/bk3a3Uou';

    const typeFormURL = new URL(typeFormEndpoint);
    typeFormURL.searchParams.set('locale', CookieService.get('locale'));
    typeFormURL.searchParams.set('source_cat', 'product');
    if (source) {
      typeFormURL.searchParams.set('source', source);
    }

    if (feat) {
      typeFormURL.searchParams.set('feat', feat);
    }

    if (variant) {
      typeFormURL.searchParams.set('variant', variant);
    }

    typeFormURL.searchParams.set('session_id', CookieService.get('quizizz_uid'));
    if ($store()) {
      const user = new UserService($store().getters['root/user'] ?? {});
      if (user && user.id) {
        typeFormURL.searchParams.set('user_id', user.id);
      }
      if (user && user.email) {
        typeFormURL.searchParams.set('qfw_email', user.email);
      }
      else {
        typeFormURL.searchParams.set('qfw_email', 'unknown');
      }

      typeFormURL.searchParams.set('country', $store().getters['root/serverData'].requestCountry);
    }

    const tabLocation = openInNewTab ? '_blank' : '_self';
    window.open(typeFormURL, tabLocation);
  }
}

export function OpenQFWUpdateSubscriptionPage(type = Subscription.UPDATE_SUBSCRIPTION_TYPE.UPGRADE, queryParams = {}) {
  getRouter().push({
    path: '/admin/subscription/update',
    query: {
      type,
      ...queryParams,
    },
  });
}

export function getCorporateSSORedirectionPage({ errorType }) {
  const hasRedirect = getRouter()?.currentRoute?.value?.query?.q;

  let queryString = {};

  if (window.location.search.length > 1) {
    queryString = Object.fromEntries(new URLSearchParams(window.location.search));
    if (!hasRedirect) {
      queryString.q = getRouter()?.currentRoute?.value?.path;
    }
  } else {
    queryString.q = getRouter()?.currentRoute?.value?.path;
  }

  queryString.error = errorType;

  getRouter().push({
    path: '/signup/sso',
    query: {
      ...queryString,
    },
  });
}
